/**
 * Contract function name → adapter. Adapters only translate JSON ⇄ domain values and call
 * the engine; no maths lives here. Output shapes mirror ContractRegistry.swift exactly
 * (field names, and null rather than an omitted key).
 */
import { formatDay } from './core/calendar.ts';
import { appreciatedValue } from './calculations/appreciation.ts';
import { calculatePayments } from './calculations/payment-calculator.ts';
import { countOccurrences, countOccurrencesUpTo, occurrenceDate } from './calculations/occurrences.ts';
import { debtVsInvestmentInsight } from './calculations/debt-vs-investment.ts';
import { calculateTotalInvestment } from './calculations/investment-projector.ts';
import { compareMetrics, type ComparableMetrics } from './calculations/metrics-comparison.ts';
import {
  decodeAsset, decodeFrequency, decodeTransaction, decodeTransactions, decodeWindow, encodeSeries, encodeWindow,
} from './codec/entities.ts';
import {
  contextFrom, decodeDay, decodeDecimal, encodeDecimal, encodeDouble, encodeOptionalDecimal,
} from './codec/scalars.ts';
import { calculateRetirement, defaultStatePension } from './features/retirement.ts';
import { timeBuckets, windowFromPlanConfiguration, windowMonths } from './forecast/window.ts';
import { assetSaleProceeds, assetValue, effectiveAnnualRate } from './models/asset.ts';
import { annualOccurrences } from './models/frequency.ts';
import { scenarioMonthlyImpact } from './models/scenario.ts';
import { effectiveInterestRate, effectivePaymentDay } from './models/transaction.ts';
import { investmentAndGoalAdapters } from './registry-investments-goals.ts';
import type * as Wire from './types/contract.generated.ts';
import { debtRegistry } from './registry-debt.ts';
import { planRegistry } from './registry-plan.ts';
import { forecastRegistry } from './registry-forecast.ts';

export type Adapter<K extends Wire.FunctionName> = (input: Wire.Functions[K]['input']) => Wire.Functions[K]['expected'];

export type Registry = { readonly [K in Wire.FunctionName]?: Adapter<K> };

type WireMetrics = Wire.Functions['MetricsCalculator.compare']['input']['planA'];

const decodeComparable = (m: WireMetrics): ComparableMetrics => ({
  finalNetWorth: decodeDecimal(m.finalNetWorth),
  peakNetWorth: decodeDecimal(m.peakNetWorth),
  finalCash: decodeDecimal(m.finalCash),
  finalInvestment: decodeDecimal(m.finalInvestment),
  finalDebt: decodeDecimal(m.finalDebt),
  averagePassiveIncome: decodeDecimal(m.averagePassiveIncome),
});

export const registry: Registry = {
  ...investmentAndGoalAdapters,

  'PaymentCalculator.calculate': (input) =>
    encodeSeries(calculatePayments(decodeTransactions(input.transactions), decodeWindow(input.window))),

  'ForecastEngine.countOccurrences': (input) => ({
    count: countOccurrences(decodeTransaction(input.transaction), decodeDay(input.from), decodeDay(input.to)),
  }),

  'MetricsCalculator.countOccurrences': (input) => ({
    count: countOccurrencesUpTo(decodeTransaction(input.transaction), decodeDay(input.date)),
  }),

  'MetricsCalculator.occurrenceDate': (input) => ({
    date: formatDay(occurrenceDate(decodeTransaction(input.transaction), input.occurrence)),
  }),

  'MetricsCalculator.compare': (input) =>
    compareMetrics(decodeComparable(input.planA), decodeComparable(input.planB)).map((row) => ({
      metric: row.metric,
      winner: row.winner,
      difference: encodeDecimal(row.difference),
    })),

  'InvestmentProjector.calculateTotalInvestment': (input) => ({
    amount: encodeDecimal(
      calculateTotalInvestment(
        decodeTransaction(input.transaction),
        input.date === undefined ? undefined : decodeDay(input.date),
        decodeTransactions(input.incomes ?? []),
      ),
    ),
  }),

  'DebtVsInvestmentCalculator.getInsight': (input) =>
    debtVsInvestmentInsight(decodeDecimal(input.investmentReturn), decodeDecimal(input.debtInterestRate)),

  'ForecastWindow.fromPlanConfiguration': (input) => {
    const window = windowFromPlanConfiguration(input.timeframeYears, contextFrom(input).now);
    return {
      window: encodeWindow(window),
      months: windowMonths(window),
      bucketStarts: timeBuckets(window).map((bucket) => formatDay(bucket.start)),
    };
  },

  'Transaction.fields': (input) => {
    const transaction = decodeTransaction(input.transaction);
    return {
      effectiveInterestRate: encodeOptionalDecimal(effectiveInterestRate(transaction, decodeDay(input.date))),
      effectivePaymentDay: effectivePaymentDay(transaction),
      annualOccurrences: encodeDouble(annualOccurrences(transaction.frequency)),
    };
  },

  'Plan.calculateAppreciatedValue': (input) => ({
    value: encodeDecimal(
      appreciatedValue(
        decodeDecimal(input.originalValue),
        decodeDecimal(input.appreciationRate),
        decodeDay(input.purchaseDate),
        decodeDay(input.date),
      ),
    ),
  }),

  'Asset.value': (input) => {
    const asset = decodeAsset(input.asset);
    const date = decodeDay(input.date);
    return {
      value: encodeDecimal(assetValue(asset, date)),
      saleProceeds: encodeDecimal(assetSaleProceeds(asset, date)),
      effectiveAnnualRate: encodeDecimal(effectiveAnnualRate(asset.valueTrajectory)),
    };
  },

  'Scenario.monthlyImpact': (input) => ({
    monthlyImpact: encodeDecimal(
      scenarioMonthlyImpact(
        input.adjustments.map((a) => ({
          type: a.type,
          amount: decodeDecimal(a.amount),
          frequency: decodeFrequency(a.frequency),
        })),
      ),
    ),
  }),

  RetirementCalculation: (input) => {
    const r = calculateRetirement({
      retirementAge: input.retirementAge,
      currentAge: input.currentAge,
      monthlyNeed: decodeDecimal(input.monthlyNeed),
      workplacePension: decodeDecimal(input.workplacePension),
      statePension: decodeDecimal(input.statePension),
      otherIncome: decodeDecimal(input.otherIncome),
      currentInvestments: decodeDecimal(input.currentInvestments),
    });
    return {
      yearsUntilRetirement: r.yearsUntilRetirement,
      guaranteedMonthlyIncome: encodeDecimal(r.guaranteedMonthlyIncome),
      monthlyGap: encodeDecimal(r.monthlyGap),
      monthlySurplus: encodeDecimal(r.monthlySurplus),
      requiredPot: encodeDecimal(r.requiredPot),
      shortfall: encodeDecimal(r.shortfall),
      progressPercent: encodeDouble(r.progressPercent),
      defaultStatePension:
        input.currencyCode === undefined || input.currencyCode === null
          ? null
          : encodeDecimal(defaultStatePension(input.currencyCode)),
    };
  },

  ...debtRegistry,
  ...planRegistry,
  ...forecastRegistry,
};
